<template>
  <div class="reports-toolbar">
    <select
      :value="timeRange"
      @change="onTimeRangeChange"
      class="rpt-select"
    >
      <optgroup label="Real-time">
        <option value="1min">Previous Minute</option>
      </optgroup>
      <optgroup label="Hours">
        <option value="1h">Previous Hour</option>
        <option value="12h">Previous 12 Hours</option>
        <option value="24h">Previous 24 Hours</option>
      </optgroup>
      <optgroup label="Days">
        <option value="today">Today</option>
        <option value="yesterday">Yesterday</option>
      </optgroup>
      <optgroup label="Longer">
        <option value="1w">Previous 7 Days</option>
        <option value="1m">Previous 30 Days</option>
        <option value="quarter">This Quarter</option>
        <option value="ytd">Year to Date</option>
      </optgroup>
    </select>

    <slot name="filters" />

    <button
      class="rpt-btn rpt-btn-primary"
      :disabled="updateDisabled"
      @click="$emit('update')"
    >
      <span v-if="isLoading" class="rpt-spinner" />
      <span v-else>Update</span>
    </button>

    <button
      class="rpt-btn rpt-btn-export"
      :disabled="!hasResponse"
      title="Export to Excel"
      @click="$emit('export')"
    >
      <ExportIcon class="rpt-export-icon" />
      Export
    </button>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import type { ProofOfPlayTimeRangePreset } from '@/helpers/api/metrics';
import ExportIcon from '@/components/icons/ExportIcon.vue';

const props = defineProps<{
  timeRange: ProofOfPlayTimeRangePreset;
  isLoading: boolean;
  isDirty: boolean;
  isStale: boolean;
  hasResponse: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:timeRange', value: ProofOfPlayTimeRangePreset): void;
  (e: 'update'): void;
  (e: 'export'): void;
}>();

// Allow Update when filters changed, data has aged past its staleness threshold,
// or no data has loaded yet.
const updateDisabled = computed(
  () => props.isLoading || (!props.isDirty && !props.isStale && props.hasResponse),
);

function onTimeRangeChange(event: Event) {
  const value = (event.target as HTMLSelectElement).value as ProofOfPlayTimeRangePreset;
  emit('update:timeRange', value);
}
</script>

<style scoped>
@import '@/views/ReportsView/styles/reports-shared.css';

.reports-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.rpt-select {
  appearance: none;
  -webkit-appearance: none;
  background: #f9fafb;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.4rem 0.75rem;
  font-size: 0.85rem;
  color: #374151;
  transition: border-color 0.15s;
  padding-right: 2rem;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M3 4.5L6 8l3-3.5H3z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  cursor: pointer;
}

.rpt-select:hover {
  border-color: #9ca3af;
}

.rpt-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

.rpt-btn-export {
  background: #f9fafb;
}

.rpt-export-icon {
  width: 16px;
  height: 16px;
}
</style>
